import React, { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import classNames from 'classnames'

import { Icons } from '@/components/Shared/icons'
import DefaultButton from '@/components/Shared/DefaultButton'
import { ModalProps, ModalType } from '@/types'

interface DefaultModalProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  modalProps: ModalProps
  className?: string
  XCloseButton?: boolean
}

const DefaultModal: React.FC<DefaultModalProps> = ({
  open,
  setOpen,
  modalProps,
  className,
  XCloseButton = true,
}) => {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" data-default-modal="true" className="relative z-[62]" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-coolGray-80 bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  'relative transform rounded-lg bg-white px-[24px] text-left shadow-xl transition-all lg:my-8 lg:w-full lg:max-w-lg sm:px-5',
                  className
                )}
              >
                {
                  <div className="py-[24px]">
                    <div className={'flex items-center justify-between'}>
                      <div className={'text-[20px] font-medium leading-[2rem] text-gray-900'}>
                        {modalProps.title}
                      </div>
                      {XCloseButton && (
                        <Icons.Close
                          className="w-6 h-6 cursor-pointer"
                          onClick={() => setOpen(false)}
                        />
                      )}
                    </div>
                    <div className="mt-5">
                      <div>
                        {typeof modalProps.body == 'string' ? (
                          <div
                            className="text-label-neutral text-sm"
                            dangerouslySetInnerHTML={{ __html: modalProps.body as string }}
                          />
                        ) : (
                          modalProps.body
                        )}
                      </div>
                      <div className="mt-6 flex justify-end gap-x-2">
                        <DefaultButton
                          type="button"
                          content={{ text: modalProps.cancelText || 'Cancel' }}
                          extendClass={false}
                          className="text-label-neutral text-[15px] leading-6 rounded border border-line-neutral bg-white px-3 py-[7px] disabled:opacity-50 disabled:cursor-not-allowed"
                          handleClick={() => setOpen(false)}
                          disabled={modalProps.isLoading || modalProps.secondaryAction?.isLoading}
                        />
                        {modalProps.secondaryAction ? (
                          <DefaultButton
                            type="button"
                            content={{
                              text: modalProps.secondaryAction.isLoading
                                ? 'Loading...'
                                : modalProps.secondaryAction.text,
                            }}
                            extendClass={false}
                            className="text-status-negative text-[15px] leading-6 rounded border border-status-negative/40 bg-white px-3 py-[7px] disabled:opacity-50 disabled:cursor-not-allowed"
                            handleClick={modalProps.secondaryAction.onClick}
                            disabled={
                              modalProps.isLoading ||
                              modalProps.secondaryAction.isLoading ||
                              modalProps.secondaryAction.disabled
                            }
                          />
                        ) : null}
                        {modalProps.type != ModalType.Alert ? (
                          <DefaultButton
                            type="button"
                            content={{
                              text: modalProps.isLoading
                                ? 'Loading...'
                                : modalProps.submitText || 'Submit',
                            }}
                            extendClass={false}
                            className={classNames(
                              'text-[15px] leading-6 px-3 py-[7px] rounded disabled:bg-interaction-disabled disabled:text-label-disabled',
                              {
                                'text-white bg-primary-normal':
                                  modalProps.type === ModalType.Submit,
                                'text-white bg-status-negative':
                                  modalProps.type === ModalType.Warning,
                              }
                            )}
                            handleClick={() => {
                              modalProps.onSubmit?.()
                            }}
                            disabled={
                              modalProps.isLoading ||
                              modalProps.submitDisabled ||
                              modalProps.secondaryAction?.isLoading
                            }
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                }
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default DefaultModal
